import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CloudUpload, ExternalLink, Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { formatDate, formatDateTime, formatRM, todayKL } from "@/lib/format";
import { useAuth } from "@/lib/AuthContext";
import { DeleteConfirmModal, RemarkCell } from "@/components/portal/InternalBits";
import {
  Attachment, Badge, Button, Empty, ErrorNote, Field, Loading, Modal, Note, inputClass,
} from "@/components/portal/Ui";
import { InvoiceModal } from "@/pages/creator/CreatorInvoices";

const STATUSES = ["Submitted", "Changes requested", "Approved", "Paid"];

const AUTO_FILE_MODES = [
  { value: "default", label: "Follow the agency setting" },
  { value: "on", label: "Always file on approval" },
  { value: "off", label: "Never file automatically" },
];

/** Whether invoices on this campaign are filed to Drive the moment they are approved. */
function AutoFileSwitch({ campaignId, isAdmin }) {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/drive/campaigns/${campaignId}/auto-file`);
      setState(data);
    } catch {
      setState(null);
    }
  }, [campaignId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!state) return null;

  const change = async (mode) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/drive/campaigns/${campaignId}/auto-file`, { mode });
      setState(data);
      toast.success(
        data.effective
          ? "Approved invoices on this campaign will be filed to Drive"
          : "Approved invoices on this campaign will not be filed automatically"
      );
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="drive-auto-file-row">
      <span className="text-xs font-semibold">Filing on approval</span>
      {isAdmin ? (
        <select
          value={state.mode}
          disabled={saving}
          onChange={(e) => change(e.target.value)}
          className={`${inputClass} max-w-[240px] py-1.5 text-xs`}
          data-testid="drive-auto-file-select"
        >
          {AUTO_FILE_MODES.map((mode) => (
            <option key={mode.value} value={mode.value}>
              {mode.label}
              {mode.value === "default" ? (state.agency_default ? " (on)" : " (off)") : ""}
            </option>
          ))}
        </select>
      ) : null}
      <span className="text-xs body-text" data-testid="drive-auto-file-state">
        {state.effective
          ? "On — each invoice is copied to Drive as it is approved."
          : "Off — invoices are filed with Send to Drive."}
        {state.folder?.web_link && (
          <>
            {" · "}
            <a
              href={state.folder.web_link}
              target="_blank"
              rel="noreferrer noopener"
              className="font-semibold text-[#F2811D] hover:underline"
            >
              Campaign folder
            </a>
          </>
        )}
      </span>
    </div>
  );
}

/** Every invoice on one campaign, with approval, payment, Drive filing and version history. */
export function InvoicesSection({ campaign, creators, drive, onChanged }) {
  const campaignId = campaign.id;
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [params, setParams] = useSearchParams();
  const [invoices, setInvoices] = useState(null);
  const [error, setError] = useState("");
  const [uploadModal, setUploadModal] = useState(null);
  const [actionModal, setActionModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [labelTarget, setLabelTarget] = useState(null);
  const [sending, setSending] = useState("");

  const status = params.get("inv_status") || "";
  const invCreator = params.get("inv_creator") || "";
  const hasRemark = params.get("inv_remark") === "1";
  const showDeleted = params.get("inv_deleted") === "1";
  const openInvoiceId = params.get("invoice") || "";

  const setFilter = (key, value) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true }
    );

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/invoices", {
        params: {
          campaign_id: campaignId,
          status: status || undefined,
          creator_id: invCreator || undefined,
          has_remark: hasRemark || undefined,
          include_deleted: showDeleted || undefined,
        },
      });
      setInvoices(data);
      setError("");
    } catch (e) {
      setError(apiError(e));
      setInvoices((prev) => prev || []);
    }
  }, [campaignId, status, invCreator, hasRemark, showDeleted]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = async () => {
    await load();
    await onChanged?.();
  };

  const approve = async (invoice) => {
    try {
      await api.post(`/invoices/${invoice.id}/approve`);
      toast.success(`Invoice ${invoice.invoice_number} approved`);
      await refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveRemark = async (invoice, remark) => {
    try {
      await api.put(`/invoices/${invoice.id}/remark`, { remark });
      toast.success(remark ? "Remark saved" : "Remark cleared");
      await refresh();
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    }
  };

  const removeInvoice = async (reason) => {
    try {
      const { data } = await api.delete(`/invoices/${deleteTarget.id}`, { data: { reason } });
      toast.success(
        data.notifications_cancelled
          ? "Invoice deleted — queued WhatsApp message cancelled"
          : "Invoice deleted"
      );
      await refresh();
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    }
  };

  const restoreInvoice = async (invoice) => {
    try {
      await api.post(`/invoices/${invoice.id}/restore`);
      toast.success("Invoice restored");
      await refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const sendToDrive = async (invoice, force = false) => {
    setSending(invoice.id);
    try {
      const { data } = await api.post(
        `/drive/invoices/${invoice.id}/send${force ? "?force=true" : ""}`
      );
      toast.success(data.skipped ? data.message : `${invoice.invoice_number} filed to Drive`);
      await refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending("");
    }
  };

  const sendAllToDrive = async () => {
    setSending("all");
    try {
      const { data } = await api.post(`/drive/campaigns/${campaignId}/send`);
      toast.success(
        `${data.sent} filed · ${data.skipped} already there${data.failed ? ` · ${data.failed} could not be sent` : ""}`
      );
      await refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending("");
    }
  };

  const driveReady = Boolean(drive?.configured && drive?.connected && drive?.folder_id);  const openInvoice = (invoices || []).find((i) => i.id === openInvoiceId);

  if (invoices === null) return <Loading label="Loading invoices…" />;

  return (
    <div data-testid="campaign-invoices-section">
      <ErrorNote message={error} onRetry={load} />

      <div className="rounded-xl border border-[#EFEAE1] bg-[#FBFAF7] p-4" data-testid="invoice-filters">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Status">
            <select
              value={status}
              onChange={(e) => setFilter("inv_status", e.target.value)}
              className={inputClass}
              data-testid="invoice-filter-status"
            >
              <option value="">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <Field label="KOL">
            <select
              value={invCreator}
              onChange={(e) => setFilter("inv_creator", e.target.value)}
              className={inputClass}
              data-testid="invoice-filter-creator"
            >
              <option value="">All KOLs</option>
              {(creators || []).map((creator) => (
                <option key={creator.creator_id} value={creator.creator_id}>
                  {creator.creator_name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-5">
          <label className="flex items-center gap-2.5 text-sm font-semibold">
            <input
              type="checkbox"
              checked={hasRemark}
              onChange={(e) => setFilter("inv_remark", e.target.checked ? "1" : "")}
              className="h-4 w-4 accent-[#F2811D]"
              data-testid="invoice-filter-has-remark"
            />
            Has an internal remark
          </label>
          {isAdmin && (
            <label className="flex items-center gap-2.5 text-sm font-semibold">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setFilter("inv_deleted", e.target.checked ? "1" : "")}
                className="h-4 w-4 accent-[#F2811D]"
                data-testid="invoice-filter-show-deleted"
              />
              Show deleted invoices
            </label>
          )}
          <div className="ml-auto flex flex-wrap gap-2">
            {driveReady && invoices.length > 0 && (
              <Button
                variant="outline"
                busy={sending === "all"}
                onClick={sendAllToDrive}
                testId="drive-send-campaign"
              >
                <CloudUpload className="h-4 w-4" />
                Send all to Drive
              </Button>
            )}
            <Button
              testId="internal-upload-invoice-button"
              onClick={() => setUploadModal({ mode: "new", campaignId })}
            >
              <Plus className="h-4 w-4" />
              Upload on behalf
            </Button>
          </div>
        </div>
        {drive && !drive.configured && (
          <p className="mt-3 text-xs body-text" data-testid="drive-not-configured-note">
            Google Drive filing is not switched on yet — it needs the agency's Google Cloud OAuth
            Client ID and Secret with the Drive API enabled. Downloading invoices works as normal.
          </p>
        )}
        {drive?.configured && !driveReady && (
          <p className="mt-3 text-xs body-text" data-testid="drive-not-connected-note">
            {drive.connected
              ? "Pick a destination folder in the Drive settings to start filing invoices."
              : "Connect the AnyTribe Google account in the Drive settings to start filing invoices."}
          </p>
        )}
        {driveReady && <AutoFileSwitch campaignId={campaignId} isAdmin={isAdmin} />}
      </div>

      {invoices.length === 0 ? (
        <Empty
          testId="invoices-empty"
          title="No invoices match these filters"
          body="KOLs can submit an invoice at any point during a campaign."
        />
      ) : (
        <div className="mt-4">
          <div className="scroll-x">
            <table className="w-full min-w-[1180px] text-sm">
              <thead>
                <tr className="border-b border-[#EFEAE1] text-left text-xs font-bold uppercase tracking-[0.08em] text-[#8a8378]">
                  <th className="pb-2.5 pr-4">Creator</th>
                  <th className="pb-2.5 pr-4">Invoice no.</th>
                  <th className="pb-2.5 pr-4">Submitted</th>
                  <th className="pb-2.5 pr-4 text-right">Amount</th>
                  <th className="pb-2.5 pr-4">Attachment</th>
                  <th className="pb-2.5 pr-4">Status</th>
                  <th className="pb-2.5 pr-4">Due</th>
                  <th className="pb-2.5 pr-4">Paid</th>
                  <th className="pb-2.5 pr-4">Remark</th>
                  <th className="pb-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    className={`border-b border-[#F5F1EA] align-top ${
                      invoice.is_deleted
                        ? "bg-[#FDF3F3]"
                        : invoice.internal_remark
                          ? "bg-[#FFFDF6]"
                          : ""
                    }`}
                    data-testid={`invoice-row-${invoice.id}`}
                  >
                    <td className="py-3 pr-4 font-semibold">
                      {invoice.creator_name}
                      {invoice.is_consolidated && (
                        <span
                          className="mt-1 block text-[11px] font-bold uppercase tracking-[0.08em] text-[#F2811D]"
                          data-testid={`consolidated-tag-${invoice.id}`}
                        >
                          Consolidated · {invoice.lines.length} campaigns
                        </span>
                      )}
                      {(invoice.insight_flags || []).length > 0 && (
                        <span
                          className="mt-1 block text-[11px] font-bold text-[#B3261E]"
                          data-testid={`insight-flag-${invoice.id}`}
                        >
                          Insights outstanding:{" "}
                          {invoice.insight_flags.map((f) => f.campaign_name).join(", ")}
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-4">
                      <a
                        href={`?${(() => {
                          const next = new URLSearchParams(params);
                          next.set("invoice", invoice.id);
                          return next.toString();
                        })()}`}
                        onClick={(event) => {
                          if (event.metaKey || event.ctrlKey || event.shiftKey) return;
                          event.preventDefault();
                          setFilter("invoice", invoice.id);
                        }}
                        className="font-semibold text-[#F2811D] hover:underline"
                        data-testid={`invoice-open-${invoice.id}`}
                      >
                        {invoice.invoice_number}
                      </a>
                      {invoice.version > 1 && (
                        <span className="ml-1.5 text-xs body-text">v{invoice.version}</span>
                      )}
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap">
                      {formatDate(invoice.submitted_at)}
                    </td>
                    <td
                      className="py-3 pr-4 text-right font-semibold tabular"
                      data-testid={`invoice-amount-${invoice.id}`}
                    >
                      {formatRM(invoice.amount_sen)}
                    </td>
                    <td className="py-3 pr-4">
                      <Attachment file={invoice.attachment} testId={`invoice-file-${invoice.id}`} />
                      {invoice.drive_file ? (
                        <a
                          href={invoice.drive_file.web_link}
                          target="_blank"
                          rel="noreferrer noopener"
                          data-testid={`drive-filed-${invoice.id}`}
                          className="mt-1 inline-flex items-center gap-1.5 text-[11px] body-text hover:underline"
                        >
                          Filed to Drive · {formatDate(invoice.drive_file.sent_at)}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : null}
                      {!invoice.drive_file && invoice.drive_auto_error && (
                        <p
                          className="mt-1 text-[11px] text-[#9B2C2C]"
                          data-testid={`drive-auto-error-${invoice.id}`}
                        >
                          Not filed automatically: {invoice.drive_auto_error}
                        </p>
                      )}
                      {driveReady && invoice.attachment && !invoice.is_deleted && (
                        <button
                          type="button"
                          disabled={sending === invoice.id}
                          onClick={() => sendToDrive(invoice, Boolean(invoice.drive_file))}
                          data-testid={`drive-send-${invoice.id}`}
                          className="mt-1 block text-left text-[11px] font-semibold text-[#2C4D8F] hover:underline disabled:opacity-50"
                        >
                          {invoice.drive_file ? "Send a fresh copy" : "Send to Drive"}
                        </button>
                      )}
                      {isAdmin && invoice.attachment && (
                        <button
                          type="button"
                          onClick={() =>
                            setLabelTarget({
                              invoice,
                              version:
                                invoice.versions.find((v) => v.version === invoice.version) ||
                                invoice.versions[0],
                            })
                          }
                          data-testid={`edit-attachment-label-${invoice.id}`}
                          className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#F2811D] hover:underline"
                        >
                          <Pencil className="h-3 w-3" />
                          Rename label
                        </button>
                      )}
                    </td>
                    <td className="py-3 pr-4">
                      <Badge testId={`invoice-status-${invoice.id}`}>{invoice.status}</Badge>
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap">{formatDate(invoice.due_date)}</td>
                    <td className="py-3 pr-4 whitespace-nowrap">
                      {invoice.paid_at ? formatDate(invoice.paid_at) : "—"}
                    </td>
                    <td className="py-3 pr-4">
                      <RemarkCell
                        value={invoice.internal_remark}
                        byName={invoice.internal_remark_by_name}
                        at={invoice.internal_remark_at}
                        onSave={(text) => saveRemark(invoice, text)}
                        testId={`invoice-remark-${invoice.id}`}
                      />
                    </td>
                    <td className="py-3">
                      <div className="flex flex-col gap-1.5">
                        {!invoice.is_deleted &&
                          ["Submitted", "Changes requested"].includes(invoice.status) && (
                            <>
                              <button
                                type="button"
                                onClick={() => approve(invoice)}
                                data-testid={`approve-invoice-${invoice.id}`}
                                className="text-left text-xs font-semibold text-[#1B6B3A] hover:underline"
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                onClick={() => setActionModal({ kind: "request-changes", invoice })}
                                data-testid={`request-changes-${invoice.id}`}
                                className="text-left text-xs font-semibold text-[#B3261E] hover:underline"
                              >
                                Request changes
                              </button>
                            </>
                          )}
                        {invoice.status === "Approved" && !invoice.is_deleted && (
                          <>
                            <button
                              type="button"
                              onClick={() => setActionModal({ kind: "mark-paid", invoice })}
                              data-testid={`mark-paid-${invoice.id}`}
                              className="text-left text-xs font-semibold text-[#1B6B3A] hover:underline"
                            >
                              Mark as paid
                            </button>
                            <button
                              type="button"
                              onClick={() => setActionModal({ kind: "return", invoice })}
                              data-testid={`return-invoice-${invoice.id}`}
                              className="text-left text-xs font-semibold text-[#B3261E] hover:underline"
                            >
                              Return for correction
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          onClick={() => setFilter("invoice", invoice.id)}
                          data-testid={`invoice-history-${invoice.id}`}
                          className="text-left text-xs font-semibold text-[#F2811D] hover:underline"
                        >
                          History
                        </button>
                        {isAdmin && !invoice.is_deleted && (
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(invoice)}
                            data-testid={`delete-invoice-${invoice.id}`}
                            className="inline-flex items-center gap-1.5 text-left text-xs font-semibold text-[#B3261E] hover:underline"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete invoice
                          </button>
                        )}
                        {invoice.is_deleted && (
                          <div className="text-xs" data-testid={`invoice-deleted-note-${invoice.id}`}>
                            <p className="font-semibold text-[#B3261E]">
                              Deleted by {invoice.deleted_by_name}
                            </p>
                            <p className="body-text">{invoice.delete_reason}</p>
                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => restoreInvoice(invoice)}
                                data-testid={`restore-invoice-${invoice.id}`}
                                className="mt-1 inline-flex items-center gap-1.5 font-semibold text-[#1B6B3A] hover:underline"
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                                Restore
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs body-text">
            Payment tracking records payments made outside the app. Corrected invoices stay on the
            same record as new versions. Filing to Drive copies the KOL's uploaded document — the
            original stays here as the record.
          </p>
        </div>
      )}

      <InvoiceModal
        state={uploadModal}
        campaigns={[campaign]}
        creatorsByCampaign={{ [campaignId]: creators || [] }}
        onClose={() => setUploadModal(null)}
        onSaved={async () => {
          setUploadModal(null);
          await refresh();
        }}
      />

      <ActionModal
        state={actionModal}
        onClose={() => setActionModal(null)}
        onSaved={async () => {
          setActionModal(null);
          await refresh();
        }}
      />

      <HistoryModal invoice={openInvoice} onClose={() => setFilter("invoice", "")} />

      <DeleteConfirmModal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Delete this invoice?"
        summary={
          deleteTarget
            ? `${deleteTarget.invoice_number} · ${deleteTarget.creator_name} · ${formatRM(deleteTarget.amount_sen)}`
            : ""
        }
        warning="The invoice and all its versions disappear from internal and KOL views and from the outstanding and paid figures. Any queued WhatsApp payment message is cancelled. An admin can restore it from the 'Show deleted invoices' view."
        onConfirm={removeInvoice}
        testId="delete-invoice-modal"
      />

      <AttachmentLabelModal
        state={labelTarget}
        onClose={() => setLabelTarget(null)}
        onSaved={refresh}
      />

      <PaymentNotifications
        campaignId={campaignId}
        reloadKey={invoices.map((i) => `${i.id}:${i.status}`).join("|")}
      />
    </div>
  );
}

const NOTIFY_TONES = { Sent: "green", Pending: "warn", Failed: "red", Blocked: "red" };

function PaymentNotifications({ campaignId, reloadKey }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { campaign_id: campaignId } });
      setRows(data);
    } catch (e) {
      setError(apiError(e));
    }
  }, [campaignId]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const act = async (row, action, body) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/notifications/${row.id}/${action}`, body || {});
      toast.success(
        action === "sent" ? "Marked as sent" : action === "retry" ? "Ready to try again" : "Recorded"
      );
      setOpen(null);
      setReason("");
      await load();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copyMessage = async (row) => {
    try {
      await navigator.clipboard.writeText(row.message);
      toast.success("Message copied");
    } catch {
      toast.error("Copy failed — open the message and copy it manually");
    }
  };

  if (rows !== null && rows.length === 0) return null;

  return (
    <div className="mt-6 border-t border-[#EFEAE1] pt-5" data-testid="payment-notifications">
      <h3 className="text-sm font-extrabold">Payment WhatsApp notifications</h3>
      <p className="mt-1 text-xs body-text">
        Marking an invoice paid queues one message. Open WhatsApp, send it from the AnyTribe
        Business number, then record the outcome here.
      </p>
      <ErrorNote message={error} />
      {rows === null ? (
        <Loading />
      ) : (
        <ul className="mt-3 space-y-3">
          {rows.map((row) => (
            <li
              key={row.id}
              className="rounded-xl border border-[#EDE7DC] p-3.5"
              data-testid={`notification-row-${row.id}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-extrabold">
                    {row.creator_name} · {row.invoice_number}
                  </p>
                  <p className="mt-0.5 text-xs body-text">
                    {formatRM(row.amount_sen)} · {row.phone || "no phone on file"}
                  </p>
                </div>
                <Badge tone={NOTIFY_TONES[row.status]} testId={`notification-status-${row.id}`}>
                  {row.status}
                </Badge>
              </div>
              {row.blocked_reason && (
                <p
                  className="mt-2.5 text-xs font-semibold text-[#8C2F2F]"
                  data-testid={`notification-blocked-${row.id}`}
                >
                  {row.blocked_reason}
                </p>
              )}
              {row.status === "Sent" && (
                <p className="mt-2.5 text-xs body-text" data-testid={`notification-sent-by-${row.id}`}>
                  Sent by {row.sent_by_name} on {formatDateTime(row.sent_at)}
                </p>
              )}
              {row.status === "Failed" && (
                <p className="mt-2.5 text-xs body-text">
                  Last attempt failed: {row.last_error} ({row.attempts} attempt
                  {row.attempts === 1 ? "" : "s"})
                </p>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => setOpen(row)} testId={`notification-view-${row.id}`}>
                  View message
                </Button>
                {row.wa_link && row.status !== "Sent" && (
                  <a
                    href={row.wa_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center rounded-full bg-[#F2811D] px-3.5 py-1.5 text-xs font-bold text-white transition-colors hover:bg-[#D96E12]"
                    data-testid={`notification-open-whatsapp-${row.id}`}
                  >
                    Open in WhatsApp
                  </a>
                )}
                {["Pending", "Failed"].includes(row.status) && (
                  <>
                    <Button onClick={() => act(row, "sent")} testId={`notification-mark-sent-${row.id}`}>
                      Mark as sent
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setOpen(row);
                        setReason("");
                      }}
                      testId={`notification-mark-failed-${row.id}`}
                    >
                      Couldn't send
                    </Button>
                  </>
                )}
                {["Failed", "Blocked"].includes(row.status) && (
                  <Button variant="outline" onClick={() => act(row, "retry")} testId={`notification-retry-${row.id}`}>
                    Try again
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={Boolean(open)}
        onClose={() => setOpen(null)}
        title={`Message for ${open?.creator_name || ""}`}
        testId="notification-modal"
      >
        <div className="space-y-4">
          <pre
            className="whitespace-pre-wrap rounded-xl bg-[#F7F4EF] p-3.5 text-sm leading-relaxed"
            data-testid="notification-message-text"
          >
            {open?.message}
          </pre>
          <Button variant="outline" onClick={() => copyMessage(open)} testId="notification-copy-message">
            Copy message
          </Button>
          {["Pending", "Failed"].includes(open?.status) && (
            <Field label="Couldn't send it? Record why" hint="Kept on the record for this invoice.">
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className={inputClass}
                placeholder="e.g. number not on WhatsApp"
                data-testid="notification-reason-input"
              />
            </Field>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(null)} testId="notification-modal-close">
              Close
            </Button>
            {["Pending", "Failed"].includes(open?.status) && (
              <>
                <Button
                  variant="outline"
                  busy={busy}
                  onClick={() => act(open, "failed", { reason })}
                  testId="notification-record-failure"
                >
                  Record failure
                </Button>
                <Button busy={busy} onClick={() => act(open, "sent")} testId="notification-confirm-sent">
                  I've sent it
                </Button>
              </>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}

function AttachmentLabelModal({ state, onClose, onSaved }) {
  const invoice = state?.invoice;
  const [labels, setLabels] = useState({});
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!invoice) return;
    const next = {};
    (invoice.versions || []).forEach((version) => {
      next[version.id] = version.attachment?.label || "";
    });
    setLabels(next);
  }, [invoice]);

  const save = async (version) => {
    setBusy(version.id);
    try {
      await api.patch(`/invoices/${invoice.id}/versions/${version.id}/attachment-label`, {
        label: labels[version.id] || "",
      });
      toast.success("Attachment label updated");
      await onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <Modal
      open={Boolean(invoice)}
      onClose={onClose}
      title="Rename invoice attachments"
      wide
      testId="attachment-label-modal"
    >
      <div className="space-y-4">
        <Note tone="neutral">
          Only the display label changes — the uploaded file itself is untouched and its real
          filename stays visible. KOLs and clients see the label. Every rename is recorded in the
          activity log.
        </Note>
        {(invoice?.versions || [])
          .filter((version) => version.attachment)
          .map((version) => (
            <div
              key={version.id}
              className="rounded-xl border border-[#EFEAE1] p-3.5"
              data-testid={`label-version-${version.id}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-semibold">Version {version.version}</span>
                <span className="text-xs body-text">{version.attachment.original_filename}</span>
              </div>
              <div className="mt-2.5 flex flex-wrap items-end gap-2.5">
                <div className="min-w-[240px] flex-1">
                  <Field label="Label" hint="Leave empty to fall back to the real filename.">
                    <input
                      value={labels[version.id] || ""}
                      onChange={(e) =>
                        setLabels((prev) => ({ ...prev, [version.id]: e.target.value }))
                      }
                      className={inputClass}
                      placeholder="e.g. Nur Aisyah — Oct invoice"
                      data-testid={`label-input-${version.id}`}
                    />
                  </Field>
                </div>
                <Button busy={busy === version.id} onClick={() => save(version)} testId={`label-save-${version.id}`}>
                  Save
                </Button>
              </div>
            </div>
          ))}
        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose} testId="attachment-label-close">
            Done
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function HistoryModal({ invoice, onClose }) {
  if (!invoice) return null;
  return (
    <Modal open onClose={onClose} title="Invoice history" wide testId="invoice-history-modal">
      <div className="space-y-4">
        <p className="text-sm body-text">
          {invoice.invoice_number} · {invoice.creator_name} ·{" "}
          {(invoice.campaign_names || [invoice.campaign_name]).join(" + ")}
        </p>
        {invoice.lines?.length > 1 && (
          <div className="rounded-xl border border-[#EFEAE1] bg-[#FBF9F5] p-4" data-testid="invoice-breakdown">
            <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]">
              Itemised breakdown
            </h3>
            <ul className="mt-2 space-y-1.5 text-sm">
              {invoice.lines.map((line) => {
                const flag = (invoice.insight_flags || []).find(
                  (f) => f.campaign_id === line.campaign_id
                );
                return (
                  <li
                    key={line.campaign_id}
                    className="flex flex-wrap justify-between gap-2"
                    data-testid={`breakdown-${line.campaign_id}`}
                  >
                    <span>
                      {line.campaign_name}
                      <span
                        className={`ml-2 text-xs font-semibold ${flag ? "text-[#B3261E]" : "text-[#1B6B3A]"}`}
                      >
                        {flag ? "insights outstanding" : "insights up to date"}
                      </span>
                    </span>
                    <span className="font-semibold tabular">{formatRM(line.amount_sen)}</span>
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 border-t border-[#EFEAE1] pt-2 text-sm font-extrabold">
              Total {formatRM(invoice.amount_sen)} — counted once
            </p>
          </div>
        )}
        <div>
          <h3 className="text-sm font-bold uppercase tracking-[0.1em] text-[#8a8378]">Versions</h3>
          <ul className="mt-3 space-y-2.5">
            {invoice.versions.map((version) => (
              <li
                key={version.id}
                className="rounded-xl border border-[#EFEAE1] p-3.5"
                data-testid={`invoice-version-${version.id}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold">Version {version.version}</span>
                  <span className="font-semibold tabular">{formatRM(version.amount_sen)}</span>
                </div>
                <p className="mt-1 text-xs body-text">
                  Invoice date {formatDate(version.invoice_date)} · submitted{" "}
                  {formatDateTime(version.created_at)} by {version.submitted_by_name}
                </p>
                <div className="mt-2">
                  <Attachment file={version.attachment} testId={`version-file-${version.id}`} />
                </div>
                {version.note && <p className="mt-2 text-sm body-text">{version.note}</p>}
              </li>
            ))}
          </ul>
        </div>
        {invoice.feedback?.length > 0 && (
          <div>
            <h3 className="text-sm font-bold uppercase tracking-[0.1em] text-[#8a8378]">Feedback</h3>
            <ul className="mt-3 space-y-2">
              {invoice.feedback.map((note) => (
                <li key={note.id}>
                  <Note tone="red">
                    <strong>{note.author_name}</strong> · {formatDateTime(note.created_at)}
                    <br />
                    {note.message}
                  </Note>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="rounded-xl bg-[#FBFAF7] p-4 text-sm body-text">
          <p>Submitted by {invoice.submitted_by_name}</p>
          {invoice.approved_by_name && (
            <p>
              Approved by {invoice.approved_by_name} on {formatDate(invoice.approved_at)}
            </p>
          )}
          {invoice.payment_recorded_by_name && (
            <p>
              Payment recorded by {invoice.payment_recorded_by_name} on {formatDate(invoice.paid_at)}
              {invoice.payment_reference ? ` · ref ${invoice.payment_reference}` : ""}
            </p>
          )}
          {invoice.drive_file && (
            <p data-testid="history-drive-file">
              Filed to Google Drive by {invoice.drive_file.sent_by_name} on{" "}
              {formatDate(invoice.drive_file.sent_at)} ·{" "}
              <a
                href={invoice.drive_file.web_link}
                target="_blank"
                rel="noreferrer noopener"
                className="font-semibold text-[#F2811D] hover:underline"
              >
                open in Drive
              </a>
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}

function ActionModal({ state, onClose, onSaved }) {
  const [message, setMessage] = useState("");
  const [paymentDate, setPaymentDate] = useState(todayKL());
  const [reference, setReference] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setMessage("");
    setPaymentDate(todayKL());
    setReference("");
    setConfirmed(false);
    setError("");
  }, [state]);

  const invoice = state?.invoice;

  const submit = async (event) => {
    event.preventDefault();
    if (busy || !invoice) return;
    setBusy(true);
    setError("");
    try {
      if (state.kind === "request-changes") {
        await api.post(`/invoices/${invoice.id}/request-changes`, { message: message.trim() });
        toast.success("Changes requested");
      } else if (state.kind === "return") {
        await api.post(`/invoices/${invoice.id}/return-for-correction`, { message: message.trim() });
        toast.success("Invoice returned for correction");
      } else if (state.kind === "mark-paid") {
        if (!confirmed) {
          setError("Please confirm the payment has actually been made.");
          setBusy(false);
          return;
        }
        await api.post(`/invoices/${invoice.id}/mark-paid`, {
          payment_date: paymentDate,
          payment_reference: reference.trim() || null,
        });
        toast.success("Payment recorded");
      }
      onSaved();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const titles = {
    "request-changes": "Request invoice changes",
    return: "Return approved invoice",
    "mark-paid": "Record invoice payment",
  };

  return (
    <Modal
      open={Boolean(state)}
      onClose={onClose}
      title={titles[state?.kind] || ""}
      testId="invoice-action-modal"
    >
      <form onSubmit={submit} className="space-y-4" data-testid="invoice-action-form">
        <ErrorNote message={error} />
        {state?.kind === "mark-paid" ? (
          <>
            <Note tone="orange">
              This records a payment made outside the app for {invoice?.invoice_number} —{" "}
              {formatRM(invoice?.amount_sen)}. The creator will see it as paid.
            </Note>
            <Field label="Payment date" required>
              <input
                type="date"
                required
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className={inputClass}
                data-testid="payment-date-input"
              />
            </Field>
            <Field label="Payment reference (optional)">
              <input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className={inputClass}
                data-testid="payment-reference-input"
              />
            </Field>
            <label className="flex items-start gap-2.5 text-sm">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                data-testid="payment-confirm-checkbox"
                className="mt-1"
              />
              I confirm this payment has been made and the details above are correct.
            </label>
          </>
        ) : (
          <>
            {state?.kind === "return" && (
              <Note tone="orange">
                This moves an already-approved invoice back to "Changes requested". A reason is
                recorded on the invoice and shown to the creator.
              </Note>
            )}
            <Field label="Feedback for the creator" required>
              <textarea
                rows={4}
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className={inputClass}
                data-testid="invoice-action-message-input"
              />
            </Field>
          </>
        )}
        <div className="flex justify-end gap-2.5">
          <Button variant="outline" onClick={onClose} testId="invoice-action-cancel">
            Cancel
          </Button>
          <Button type="submit" busy={busy} testId="invoice-action-submit">
            {state?.kind === "mark-paid" ? "Record payment" : "Submit"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
